import DateFormatter from 'utils/date/DateFormatter';
import {
    getFirstDayOfWeekMoment,
    getLastDayOfWeekMoment,
    getFirstDayOfMonthMoment,
    getLastDayOfMonthMoment,
    getFirstDayOfYearMoment,
    getLastDayOfYearMoment
} from 'utils/DateUtils';

import { getParentTypeDisplayOrderIndex } from 'utils/ActivityTypesUtil';
import {
    ColorBluePrimary,
    ColorVioletDark3,
    ColorOrangeDark2,
    ColorGreenDark2,
    ColorDarkuiAccent2,
    ColorRedLight2,
    ColorBlueDark1
} from 'ui-connect/styles/default-theme';

export const getParentActivityType = (formattedActivityTypes, activityTypeId) => {
    for (let type of formattedActivityTypes) {
        if (type.value === activityTypeId) {
            return {
                ...type,
                typeId: type.value,
                displayOrder: getParentTypeDisplayOrderIndex(type.typeKey)
            };
        } else {
            for (let child of type.children) {
                if (child.value === activityTypeId) {
                    return {
                        ...type,
                        typeId: type.value
                    };
                }
            }
        }
    }
};

export const getActivityType = (formattedActivityTypes, activityTypeId) => {
    for (let type of formattedActivityTypes) {
        if (type.value === activityTypeId) {
            return {
                ...type,
                typeId: type.value
            };
        } else {
            for (let child of type.children) {
                if (child.value === activityTypeId) {
                    return {
                        ...child,
                        typeId: child.value
                    };
                }
            }
        }
    }
};

// Clean out activities that do not exist in formattedActivityTypes
export const getSanitizedActivitiesList = (activities, formattedActivityTypes) => {
    return activities.filter((activity) => {
        return !!getActivityType(formattedActivityTypes, activity.activityTypeId);
    });
};

const collectDataForActivities = (activities, omitMultiSport = false) => {
    let data = {
        count: 0,
        distance: 0,
        duration: 0,
        calories: 0
    };

    activities.forEach((activity) => {
        if (omitMultiSport && activity.isParent) return true;

        data.count += 1;
        if (activity.distance) data.distance += activity.distance;
        if (activity.duration) data.duration += activity.duration;
        if (activity.calories) data.calories += activity.calories;
    });

    return data;
};

export const getActivityData = (activities, formattedActivityTypes, selectedActivityTypeId) => {
    if (selectedActivityTypeId === 0) {
        // showing all activities
        return collectDataForActivities(activities, true);
    } else {
        // filter activities down to the type that should be shown
        const parent = getParentActivityType(formattedActivityTypes, selectedActivityTypeId);
        let filteredActivities = [];

        if (parent.typeId === selectedActivityTypeId) {
            // collect parent and children
            const typeIds = [parent.typeId];

            parent.children.forEach((type) => typeIds.push(type.value));

            filteredActivities = activities.filter((activity) => {
                return typeIds.includes(activity.activityTypeId);
            });
        } else {
            // only collect data for child
            filteredActivities = activities.filter((activity) => {
                return activity.activityTypeId === selectedActivityTypeId;
            });
        }

        return collectDataForActivities(filteredActivities);
    }
};

export const getYearData = (activities, formattedActivityTypes, selectedActivityTypeId) => {
    let data = {
        count: 0,
        distance: 0,
        duration: 0,
        calories: 0
    };

    if (selectedActivityTypeId === 0) {
        activities.forEach((activity) => {
            if (getActivityType(formattedActivityTypes, activity.activityTypeId).typeKey === 'multi_sport') return true;

            data.count += activity.numberOfActivities;
            data.distance += activity.totalDistance;
            data.duration += activity.totalDuration;
            data.calories += activity.totalCalories;
        });

        return data;
    } else {
        const activity = activities.find((activity) => activity.activityTypeId === selectedActivityTypeId);
        const parent = getParentActivityType(formattedActivityTypes, selectedActivityTypeId);

        if (activity) {
            data.count += activity.numberOfActivities;
            data.distance += activity.totalDistance;
            data.duration += activity.totalDuration;
            data.calories += activity.totalCalories;
        }

        if (parent.typeId === selectedActivityTypeId && parent.children.length > 0) {
            // we've selected a parent activity type. We need to also add it's child activity types to the total

            parent.children.forEach((child) => {
                const activity = activities.find((activity) => activity.activityTypeId === child.value);

                if (activity) {
                    data.count += activity.numberOfActivities;
                    data.distance += activity.totalDistance;
                    data.duration += activity.totalDuration;
                    data.calories += activity.totalCalories;
                }
            });
        }

        return data;
    }
};

export const MODAL_TYPES = {
    note: 'note',
    weight: 'weight',
    event: 'event',
    pacePro: 'pacePro',
    activity: 'activity',
    wellnessActivity: 'wellnessActivity',
    workout: 'workout',
    rescheduleATP: 'rescheduleATP',
    bloodPressure: 'bloodPressure',
    ecg: 'ecg'
};

export const ERROR_TYPES = {
    required: 0,
    invalid: 1,
    valueOutOfBounds: 2,
    event_name_required: 'event_name_validation_message',
    timeOutOfBounds: 3
};

export const ValidationMaxes = {
    calories: 65534,
    floors: 160934400.07 //same as other NonRequiredNumberValidators
};

export const DEFAULT_COLORS = {
    trainingPlan: 11,
    group: 2,
    event: 8,
    goal: 3,
    workout: 5,
    atpWorkout: 5,
    activity: {
        running: 1,
        cycling: 9,
        swimming: 7,
        diving: 2,
        multi_sport: 4,
        default: 17
    }
};

export const CALENDAR_ITEM_COLOR = {
    trainingPlan: ColorBluePrimary,
    workout: ColorVioletDark3,
    event: ColorOrangeDark2,
    goal: ColorDarkuiAccent2,
    wellnessActivity: ColorRedLight2,
    ecg: ColorRedLight2,
    jetLag: ColorOrangeDark2,
    default: ColorGreenDark2
};

export const CALENDAR_YEAR_DISPLAY_TYPES = {
    default: 1,
    event: 2,
    goal: 4,
    workout: 8,
    trainingPlan: 16,
    groupCalendar: 32,
    multisport: 64,
    healthRecording: 128
};

export const DISTANCE_UNIT_MAPPER = {
    mile: 'distance.mile',
    kilometer: 'distance.kilometer',
    yard: 'distance.yard',
    meter: 'distance.meter'
};

export const populateAdaptiveTrainingPlanPreferenceArray = (atpPlanPreference, plans) => {
    let changed = false;

    if (atpPlanPreference) {
        plans.forEach((plan) => {
            if (atpPlanPreference.filter((pref) => pref.id === plan.athletePlanId).length === 0) {
                changed = true;
                atpPlanPreference.push({
                    id: plan.athletePlanId,
                    show: true,
                    color: 1
                });
            }
        });
    }

    return changed;
};

export const getFirstAndLastDayForWeek = (dateString, firstDayOfWeek) => {
    const firstDateOfWeek = getFirstDayOfWeekMoment(firstDayOfWeek, dateString);
    const lastDateOfWeek = getLastDayOfWeekMoment(firstDayOfWeek, dateString);

    return {
        firstDay: DateFormatter.formatISODate(firstDateOfWeek.toDate()),
        lastDay: DateFormatter.formatISODate(lastDateOfWeek.toDate())
    };
};

export const getFirstAndLastDayForMonth = (dateString, firstDayOfWeek) => {
    const firstDateOfMonth = getFirstDayOfMonthMoment(dateString);
    const firstDateOfWeekForMonth = getFirstDayOfWeekMoment(firstDayOfWeek, firstDateOfMonth);

    const lastDateOfMonth = getLastDayOfMonthMoment(dateString);
    const lastDateOfWeekForMonth = getLastDayOfWeekMoment(firstDayOfWeek, lastDateOfMonth);

    return {
        firstDay: DateFormatter.formatISODate(firstDateOfWeekForMonth.toDate()),
        lastDay: DateFormatter.formatISODate(lastDateOfWeekForMonth.toDate())
    };
};

export const getFirstAndLastDayForYear = (dateString, firstDayOfWeek) => {
    const firstDateOfYear = getFirstDayOfYearMoment(dateString);
    const firstDateOfWeekForYear = getFirstDayOfWeekMoment(firstDayOfWeek, firstDateOfYear);

    const lastDateOfYear = getLastDayOfYearMoment(dateString);
    const lastDateOfWeekForYear = getLastDayOfWeekMoment(firstDayOfWeek, lastDateOfYear);

    return {
        firstDay: DateFormatter.formatISODate(firstDateOfWeekForYear.toDate()),
        lastDay: DateFormatter.formatISODate(lastDateOfWeekForYear.toDate())
    };
};

export const mergeCalendarAndEcgItems = (calendarData = [], ecgData = []) => {
    // ecgData can be null, default value is only applied when the param is missing
    // we have to check before merging, otherwise it'll add null item into the array
    if (Array.isArray(ecgData)) {
        return calendarData.concat(ecgData);
    } else {
        return calendarData;
    }
};

export const mergeCalendarAndEcgItemsForYearView = (calendarData = [], ecgData = []) => {
    return calendarData.concat(ecgData).reduce((acc, currentObj) => {
        if (!currentObj) {
            return acc;
        }

        const { date } = currentObj;

        let display = currentObj.display ? currentObj.display : CALENDAR_YEAR_DISPLAY_TYPES.healthRecording;
        const dateObjInAcc = acc.find((yearObj) => yearObj.date === date);
        if (dateObjInAcc) {
            if (!(dateObjInAcc.display & CALENDAR_YEAR_DISPLAY_TYPES.healthRecording)) {
                dateObjInAcc.display = dateObjInAcc.display + CALENDAR_YEAR_DISPLAY_TYPES.healthRecording;
            }
        } else {
            acc.push({
                date,
                display
            });
        }

        return acc;
    }, []);
};

//this method should behave similar to getParentActivityType from above but has a single list of all activityType returning the parentTypeId
//we don not have the activityTypeId to send as a param on dropdown change just the value witch is the type key
export const getParentActivityId = function(activityTypeKey, formattedActivityTypes) {
    let parentTypeId = null;

    for (let type of formattedActivityTypes) {
        if (type.typeKey === activityTypeKey) {
            parentTypeId = type.value;
        } else {
            for (let child of type.children) {
                if (child.typeKey === activityTypeKey) {
                    parentTypeId = type.value;
                }
            }
        }
    }

    return parentTypeId;
};

export const GROUP_EVENTS_COLOR = 'fcd16b';
